import type { MouseEvent, ReactNode } from 'react';
import { Children, cloneElement, isValidElement, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from '@mui/icons-material';
import { Button } from 'components/mui/common/Button';
import type { MenuItemProps } from '@mui/material';
import { Menu, MenuItem } from '@mui/material';
import { Count } from 'components/mui/common/Tabs.styles';

interface Props {
  disabled?: boolean;
  children?: ReactNode;
  activeCount?: number;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  dataTestId?: string;
  menuTestId?: string;
}

interface MenuItemElement extends MenuItemProps {
  props: {
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
  };
}

export const SettingsButton = memo(({ disabled, children, activeCount = 0, label, size, dataTestId, menuTestId }: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement<MenuItemElement>(child) && child.type === MenuItem) {
      return cloneElement(child, {
        onClick: (e: MouseEvent<HTMLLIElement>) => {
          child.props.onClick?.(e);
          handleClose();
        },
      } as Partial<MenuItemElement['props']>);
    }
    return child;
  });

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        startIcon={<Settings />}
        endIcon={activeCount > 0 ? <Count>{activeCount}</Count> : undefined}
        onClick={handleOpen}
        disabled={disabled}
        data-testid={dataTestId}>
        {label ?? t('common.settings')}
      </Button>
      <Menu disableScrollLock anchorEl={anchorEl} open={open} onClose={handleClose} data-testid={menuTestId}>
        {enhancedChildren}
      </Menu>
    </>
  );
});
